import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Shield, CheckCircle2, Loader2, Link as LinkIcon, Copy, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { requestAuditVerification, getAuditVerificationHistory } from "../../lib/api";
import { toast } from "sonner";
import { formatDateTimeTz } from "../../lib/dateUtils";

export function AuditVerifyTab({ consulta, id }) {
  const { t } = useTranslation();
  const [auditDni, setAuditDni] = useState("");
  const [auditBirthDate, setAuditBirthDate] = useState("");
  const [auditSending, setAuditSending] = useState(false);
  const [auditVerificationHistory, setAuditVerificationHistory] = useState([]);
  const [auditVerificationLink, setAuditVerificationLink] = useState(null);
  const [auditLinkCopied, setAuditLinkCopied] = useState(false);

  useEffect(() => {
    getAuditVerificationHistory(id)
      .then(res => setAuditVerificationHistory(res.data.history || []))
      .catch(() => {});
  }, [id]);

  const handleSendVerification = async () => {
    if (!auditDni.trim() || !auditBirthDate.trim()) {
      toast.error(t('results.fillDniAndBirthDate'));
      return;
    }
    setAuditSending(true);
    setAuditVerificationLink(null);
    setAuditLinkCopied(false);
    try {
      const res = await requestAuditVerification(id, { dni: auditDni.trim(), birth_date: auditBirthDate.trim() });
      toast.success(t('results.verificationLinkGenerated'));
      setAuditVerificationLink(res.data.verification_url);
      try {
        const histRes = await getAuditVerificationHistory(id);
        setAuditVerificationHistory(histRes.data.history || []);
      } catch {}
      setAuditDni("");
      setAuditBirthDate("");
    } catch (err) {
      toast.error(err.response?.data?.detail || t('results.verificationLinkError'));
    } finally {
      setAuditSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="audit-verify-tab">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-heading font-semibold text-slate-900 mb-1">{t('results.auditVerification')}</h3>
        <p className="text-sm text-slate-500 mb-6">
          {t('results.auditVerificationDesc')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('results.dniVoter')}</label>
            <input type="text" value={auditDni} onChange={(e) => setAuditDni(e.target.value)}
              placeholder="12345678A" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="audit-dni-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('results.birthDate')}</label>
            <input type="date" value={auditBirthDate} onChange={(e) => setAuditBirthDate(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="audit-birthdate-input" />
          </div>
        </div>
        <Button onClick={handleSendVerification} disabled={auditSending || !auditDni.trim() || !auditBirthDate.trim()}
          className="bg-blue-600 hover:bg-blue-700" data-testid="send-audit-verification-btn">
          {auditSending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LinkIcon className="h-4 w-4 mr-2" />}
          {t('results.generateVerificationLink')}
        </Button>

        {auditVerificationLink && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm font-medium text-green-800 mb-2 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" /> {t('results.verificationLinkGeneratedLabel')}
            </p>
            <div className="flex gap-2">
              <div className="flex-1 p-2 bg-white rounded border border-green-200 font-mono text-xs break-all select-all" data-testid="audit-verification-link">
                {auditVerificationLink}
              </div>
              <Button variant="outline" size="sm" data-testid="copy-audit-link-btn"
                onClick={() => {
                  navigator.clipboard.writeText(auditVerificationLink);
                  setAuditLinkCopied(true);
                  setTimeout(() => setAuditLinkCopied(false), 2000);
                  toast.success(t('results.linkCopied'));
                }}>
                {auditLinkCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-green-600 mt-2">
              {t('results.linkExpires24h')}
            </p>
          </div>
        )}
      </motion.div>

      {auditVerificationHistory.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">{t('results.verificationHistory')}</h3>
          <div className="overflow-x-auto">
            <table className="data-table text-sm" data-testid="audit-history-table">
              <thead>
                <tr><th>#</th><th>{t('results.sendDate')}</th><th>{t('results.statusLabel')}</th><th>{t('results.attempts')}</th></tr>
              </thead>
              <tbody>
                {auditVerificationHistory.map((entry, idx) => (
                  <tr key={idx}>
                    <td className="text-sm font-medium text-slate-700">{entry.number}</td>
                    <td className="text-sm text-slate-600">{entry.created_at ? formatDateTimeTz(entry.created_at, consulta?.entity_timezone) : '-'}</td>
                    <td>
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        entry.status === 'verified' ? 'bg-green-100 text-green-700' :
                        entry.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        entry.status === 'expired' ? 'bg-slate-100 text-slate-500' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {entry.status === 'verified' ? t('results.statusVerified') :
                         entry.status === 'pending' ? t('results.statusPending') :
                         entry.status === 'expired' ? t('results.statusExpired') :
                         t('results.statusAttemptsExhausted')}
                      </span>
                    </td>
                    <td className="text-xs text-slate-500">{entry.attempts}/{entry.max_attempts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-800 mb-1">{t('results.howAuditWorks')}</p>
            <p className="text-xs text-blue-700 leading-relaxed">
              {t('results.auditExplanation')}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
